import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Input, Table, Checkbox, Dropdown, Button } from '@gaiads/telia-react-component-library';
import { AdminProductVariant, LocalProperty, UrlImage } from '@cdaas/api-clients/dist/product-service-openapi';

import { AdminProductVariantWithKey } from '../../../../utils/types';
import { setNotification } from '../../../../store/ui';
import { productServiceAdminExternalProductsClient } from '../../../../api-client/product-service-client';

interface VariantDetailProps {
  index: number;
  variant: AdminProductVariantWithKey;
  allPropertiesInSystem: LocalProperty[];
  properties: LocalProperty[] | undefined;
  handleVariantUpdate: (updatedVariant: AdminProductVariantWithKey) => void;
  selected: boolean;
  handleSelectVariant: (variantIdOrKey: number | string) => void;
  handleDeselectVariant: (variantIdOrKey: number | string) => void;
  brand: string | undefined;
  images: UrlImage[] /* AbstractImage[] */ | undefined;
  handleImagesChange: (images: UrlImage[] /* AbstractImage[] */) => void;
  errors: {
    variantSKU: string | undefined;
    propertyValues: { propertyId: number; error: string }[] | undefined;
    duplicate: string | undefined;
  };
}

export default function VariantDetail(props: VariantDetailProps) {
  const {
    index,
    variant,
    allPropertiesInSystem,
    properties,
    handleVariantUpdate,
    selected,
    handleSelectVariant,
    handleDeselectVariant,
    brand,
    images,
    handleImagesChange,
    errors,
  } = props;
  const { t } = useTranslation('productscreen');
  const dispatch = useDispatch();

  const [fetchingVariantImages, setFetchingVariantImages] = useState<boolean>(false);
  const [addVariantImagesNoPropertyValuesError, setAddVariantImagesNoPropertyValuesError] = useState<string>('');

  const handleSkuChange = (sku: AdminProductVariant['sku']) => {
    const updatedVariant = { ...variant, sku };
    handleVariantUpdate(updatedVariant);
  };

  const handlePropertyValueChange = (newPropertyValueId: number, oldPropertyValueId: number | undefined) => {
    let updatedPropertyValueIds = variant.property_value_ids || [];
    if (oldPropertyValueId) {
      updatedPropertyValueIds = updatedPropertyValueIds.filter(id => id !== oldPropertyValueId);
    }
    updatedPropertyValueIds.push(newPropertyValueId);
    const updatedVariant = { ...variant, property_value_ids: updatedPropertyValueIds };
    handleVariantUpdate(updatedVariant);
    setAddVariantImagesNoPropertyValuesError('');
  };

  const disableAddImagesBtn = !properties || properties.length === 0;

  const fetchAndAddVariantImages = async (sku: string, propertyValueIds: number[] | undefined) => {
    if (!variant.property_value_ids || variant.property_value_ids.length === 0) {
      setAddVariantImagesNoPropertyValuesError(t('variants_detail_section.add_images_no_property_values_error'));
      return;
    }

    setFetchingVariantImages(true);
    try {
      const response = await productServiceAdminExternalProductsClient.getExternalProductImages(sku, brand || '');
      const existingImageIds = (images || []).map(image => image.id);
      const newImagesWithPropertyValues = (response.data.images as unknown as UrlImage[])
        .filter(image => !existingImageIds.includes(image.id))
        .map(image => {
          return {
            ...image,
            property_value_ids: propertyValueIds,
          };
        });
      if (newImagesWithPropertyValues.length > 0) {
        const newImagesWithId: UrlImage[] = newImagesWithPropertyValues.map(img => ({ ...img, id: img.id ?? 0 }));
        handleImagesChange([...(images || []), ...newImagesWithId]);
        dispatch(setNotification({ text: t('variants_detail_section.variant_images_add_success'), show: true, type: 'success' }));
      } else {
        dispatch(setNotification({ text: t('variants_detail_section.variant_images_add_empty'), show: true, type: 'warning' }));
      }
    } catch {
      dispatch(setNotification({ text: t('variants_detail_section.variant_images_add_error'), show: true, type: 'warning' }));
    } finally {
      setFetchingVariantImages(false);
    }
  };

  // For each different property get all the possible values (e.g. red, green etc. for Color-property) and the currently chosen value
  const propertiesInfo = (properties || []).map(property => {
    // The properties data coming with product doesn't contain all the possible values for the properties.
    // allPropertiesInSystem does have the values also
    const propertyWithValues = allPropertiesInSystem.find(prop => prop.id === property.id);
    const propertyValueOptions = propertyWithValues?.property_values || [];
    const currentValue = propertyValueOptions.find(
      propertyValue => variant.property_value_ids && variant.property_value_ids.includes(propertyValue.id),
    );
    return { propertyValueOptions, currentValue, propertyId: property.id };
  });

  return (
    <Table.Row key={variant.id}>
      <Table.Cell>
        <Checkbox
          name={`select-variant-${index}`}
          checked={selected}
          onChange={() =>
            selected ? handleDeselectVariant(variant.id || (variant.key as string)) : handleSelectVariant(variant.id || (variant.key as string))
          }
        />
      </Table.Cell>
      {propertiesInfo.map((propertyInfo, propertyIndex) => (
        <Table.Cell key={propertyIndex} data-testid={`variant-${index}-tablecell-property-${propertyIndex}`}>
          <Dropdown
            name={`variant-${index}-property-${propertyIndex}`}
            onChange={(_, option) => handlePropertyValueChange(Number(option.value), propertyInfo.currentValue?.id)}
            selectedOption={
              propertyInfo.currentValue && {
                label: propertyInfo.currentValue.value || '',
                value: propertyInfo.currentValue.id.toString(),
              }
            }
            options={propertyInfo.propertyValueOptions.map(propertyValueOption => ({
              label: propertyValueOption.value || '',
              value: propertyValueOption.id.toString(),
            }))}
            placeholder={t('variants_detail_section.property_value_dropdown_placeholder')}
            className="product-variants-details__variants-table-input"
            errorText={
              addVariantImagesNoPropertyValuesError ||
              errors.propertyValues?.find(propertyValueError => propertyValueError.propertyId === propertyInfo.propertyId)?.error ||
              errors.duplicate
            }
            title={addVariantImagesNoPropertyValuesError ? t('variants_detail_section.add_images_no_property_values_error_info') : ''}
          />
        </Table.Cell>
      ))}
      <Table.Cell>
        <Input
          name={`variant-${index}-sku`}
          value={variant.sku}
          onChange={event => handleSkuChange(event.target.value)}
          className="product-variants-details__variants-table-input"
          label=""
          errorText={errors.variantSKU}
        />
      </Table.Cell>
      <Table.Cell>
        <Button
          data-testid={`add-variant-${variant.id}-images_btn`}
          variant={'text'}
          textVariantProps={{ color: 'purple500' }}
          iconName={'plus'}
          iconOnRight
          disabled={disableAddImagesBtn}
          title={disableAddImagesBtn ? t('variants_detail_section.add_images_disabled_info') : t('variants_detail_section.add_images_info')}
          onClick={() => fetchAndAddVariantImages(variant.sku, variant.property_value_ids)}
          loading={fetchingVariantImages}
        >
          {t('variants_detail_section.fetch_and_add_images')}
        </Button>
      </Table.Cell>
    </Table.Row>
  );
}
